import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import IpdTextField from '../../../ipd/utils/IpdTextField';
import InvButton from '../InvButton';
import { IPD_COLORS } from '../../theme';

function toOptionalId(value) {
  if (value == null || value === '') return '';
  return String(value);
}

function formatGender(gender) {
  const g = String(gender || '').trim().toUpperCase();
  if (g === 'M' || g === 'MALE') return 'Male';
  if (g === 'F' || g === 'FEMALE') return 'Female';
  if (g === 'O' || g === 'OTHER' || g === 'OTHERS') return 'Other';
  return gender ? String(gender) : '';
}

function resolveAgeFromDob(dob) {
  if (!dob) return '';
  const parts = String(dob).trim().split(/[-/]/);
  if (parts.length !== 3) return '';
  let year; let month; let day;
  if (parts[0].length === 4) {
    [year, month, day] = parts.map(Number);
  } else {
    [day, month, year] = parts.map(Number);
  }
  if (!year || !month || !day) return '';
  const birth = new Date(year, month - 1, day);
  if (Number.isNaN(birth.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? String(age) : '';
}

/** Map allocated ipd_admission row → fields needed for consumption POST + UI. */
export function mapAllocatedAdmissionToPatient(row) {
  const patient = row?.patient || {};
  const name = patient.name
    || [patient.first_name, patient.last_name].filter(Boolean).join(' ').trim()
    || '';
  return {
    patient_id: toOptionalId(patient.patient_id),
    admission_id: toOptionalId(row?.admission_id),
    billing_account_id: toOptionalId(row?.billing_account_id),
    department_id: toOptionalId(row?.department_id),
    ward_id: '',
    name,
    uhid: toOptionalId(patient.uhid),
    gender: formatGender(patient.gender),
    age: resolveAgeFromDob(patient.dob),
    bed_id: toOptionalId(row?.bed_id),
    location: row?.location || '',
    doctor_name: row?.doctor_name || row?.doctors?.admitting?.name || '',
    care_context_ref: row?.admission_details?.care_context_ref || '',
  };
}

function matchesQuery(row, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const patient = row?.patient || {};
  const haystack = [
    patient.name,
    patient.first_name,
    patient.last_name,
    patient.uhid,
    patient.patient_id,
    patient.mobile,
    patient.phone,
    row?.admission_id,
    row?.bed_id,
    row?.location,
    row?.doctor_name,
    row?.billing_account_id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/**
 * Select from preloaded Allocated IPD admissions (loaded once by parent).
 * Client-side filter only — no extra GET on open/search.
 */
export default function SelectPatientDialog({
  open,
  onClose,
  onSelect,
  admissions = [],
  loading = false,
  error = null,
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filtered = useMemo(
    () => (Array.isArray(admissions) ? admissions.filter(row => matchesQuery(row, query)) : []),
    [admissions, query]
  );

  function handleSelect(row) {
    const mapped = mapAllocatedAdmissionToPatient(row);
    if (!mapped.patient_id) return;
    onSelect?.(mapped);
    onClose?.();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700 }}>
        Select Patient
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, mb: 2 }}>
          <IpdTextField
            fullWidth
            size="small"
            label="Filter by name, UHID, patient ID, bed…"
            placeholder="Type to filter"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{ color: IPD_COLORS.error.text, mb: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Typography sx={{ color: IPD_COLORS.textSecondary, py: 3, textAlign: 'center' }}>
            {admissions.length === 0
              ? 'No allocated patients found'
              : 'No patients match your filter'}
          </Typography>
        )}

        {!loading && filtered.length > 0 && (
          <Box
            sx={{
              maxHeight: 420,
              overflow: 'auto',
              p: 1,
              border: `1px solid ${IPD_COLORS.border}`,
              borderRadius: 2,
              bgcolor: IPD_COLORS.pageBg,
            }}
          >
            <Grid container spacing={1.5}>
              {filtered.map((row) => {
                const mapped = mapAllocatedAdmissionToPatient(row);
                return (
                  <Grid item xs={12} md={6} key={`${row.admission_id}-${mapped.patient_id}`}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${IPD_COLORS.border}`,
                        bgcolor: IPD_COLORS.cardBg,
                        boxShadow: 'none',
                      }}
                    >
                      <CardContent>
                        <Typography sx={{ fontWeight: 600, color: IPD_COLORS.textPrimary }}>
                          {mapped.name || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                          UHID: {mapped.uhid || '—'}
                          {' · '}
                          Gender: {mapped.gender || '—'}
                          {' · '}
                          Age: {mapped.age ? `${mapped.age} yrs` : '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                          Patient ID: {mapped.patient_id || '—'}
                          {' · '}
                          Admission: {mapped.admission_id || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                          Bed: {mapped.bed_id || '—'}
                          {mapped.location ? ` · ${mapped.location}` : ''}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <InvButton
                            size="small"
                            startIcon={<PersonSearchIcon />}
                            onClick={() => handleSelect(row)}
                          >
                            Select
                          </InvButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: IPD_COLORS.textSecondary }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
